import { Sparkle, ArrowRight, Plus, Minus } from "@phosphor-icons/react";
import { Button } from "../ui/button";
import { fmtEur } from "../../lib/api";
import { t, pickName } from "../../lib/i18n";
import { useBooking, STEP_SLOT } from "./BookingContext";

/**
 * Selector de complementos (multiplicables por cantidad, max_qty configurable).
 * Muestra subtotal en vivo y un panel inferior con total + CTA continuar.
 */
export default function AddonsSelector() {
  const {
    service, addonItems, addonQty, setAddonQty, addonsSubtotal, total,
    locale, setStep,
  } = useBooking();
  const tr = (k, params) => t(k, locale, params);
  if (!service) return null;

  return (
    <>
      <p className="text-xs uppercase tracking-[0.2em] text-gray-500">
        {pickName(service, locale)} · {fmtEur(service.price)}
      </p>
      <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tighter mt-2 text-[#1A1A1A]">
        {tr("complementos.titulo")}
      </h2>
      <p className="text-sm text-gray-600 mt-2">{tr("complementos.subtitulo")}</p>

      {addonItems.length === 0 ? (
        <div className="bg-white border border-gray-200 rounded-md p-6 text-center text-gray-500 mt-6" data-testid="pub-no-addons">
          <Sparkle size={28} weight="duotone" className="text-[#FFB703] mx-auto mb-2" />
          <p className="text-sm">{tr("complementos.sin")}</p>
        </div>
      ) : (
        <div className="space-y-2.5 mt-6">
          {addonItems.map((a) => {
            const qty = addonQty[a.id] || 0;
            const maxQ = a.max_qty || 10;
            const subtotal = qty * (a.price_per_unit || 0);
            return (
              <div key={a.id} className="bg-white border border-gray-200 rounded-md p-4 flex items-center gap-3" data-testid={`pub-addon-${a.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-[#1A1A1A]">{pickName(a, locale)}</div>
                  {a.description && <div className="text-xs text-gray-500 mt-0.5">{a.description}</div>}
                  <div className="text-xs text-gray-600 mt-1 tabular-nums">
                    {fmtEur(a.price_per_unit)} / {tr("complementos.cantidad").toLowerCase()}
                    {qty > 0 && <span className="text-[var(--ag-gold)] font-semibold ml-2">· {fmtEur(subtotal)}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button type="button"
                    onClick={() => setAddonQty({ ...addonQty, [a.id]: Math.max(0, qty - 1) })}
                    disabled={qty === 0}
                    className="w-9 h-9 rounded border border-gray-200 grid place-items-center hover:border-[#FFB703] disabled:opacity-30 disabled:cursor-not-allowed"
                    data-testid={`pub-addon-minus-${a.id}`}>
                    <Minus size={12} weight="bold" />
                  </button>
                  <div className="w-10 text-center tabular-nums font-display font-bold text-lg" data-testid={`pub-addon-qty-${a.id}`}>{qty}</div>
                  <button type="button"
                    onClick={() => setAddonQty({ ...addonQty, [a.id]: Math.min(maxQ, qty + 1) })}
                    disabled={qty >= maxQ}
                    className="w-9 h-9 rounded border border-gray-200 grid place-items-center hover:border-[#FFB703] disabled:opacity-30 disabled:cursor-not-allowed"
                    data-testid={`pub-addon-plus-${a.id}`}>
                    <Plus size={12} weight="bold" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className="mt-5 p-4 bg-[#1A1A1A] text-white rounded-md flex items-center justify-between" data-testid="pub-total-preview">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-white/60">{tr("total")}</div>
          <div className="font-display font-black text-2xl tabular-nums mt-1">{fmtEur(total)}</div>
        </div>
        <Button onClick={() => setStep(STEP_SLOT)} className="bg-[#FFB703] hover:bg-[#E5A503] text-[#1A1A1A] rounded-sm h-11 px-5" data-testid="pub-addons-continue">
          {addonsSubtotal > 0 ? tr("complementos.continuar") : tr("complementos.saltar")} <ArrowRight className="ml-1.5" weight="bold" />
        </Button>
      </div>
    </>
  );
}
